import {
  AdminApiError,
  type AdminClient,
  type ApprovalView,
  type AuditEntry,
  type GgrReport,
} from '@apx/admin-sdk';
import { fmtDateTime, fmtNumber, fmtTime } from '../format.js';

export interface HomePageDeps {
  client: AdminClient;
  integratorId: string;
  tenantName: string;
  roundEngineBaseUrl: string;
}

interface ActiveRoundLite {
  id: string;
  gameId: string;
  state: string;
  bettingClosesAt: number;
  startedAt: number;
}

export async function renderHomePage(root: HTMLElement, deps: HomePageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Overview</h1>
    <p class="page-sub">Snapshot for integrator <code>${escapeHtml(deps.tenantName)}</code>. Auto-refreshes every 15s.</p>

    <div id="home-kpis" class="kpi-grid"></div>

    <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;align-items:stretch;margin-bottom:16px">
      <div class="card" style="margin-bottom:0">
        <h2>Live round</h2>
        <div id="home-round"><p class="muted">Loading…</p></div>
      </div>
      <div class="card" style="margin-bottom:0">
        <h2>Recent activity</h2>
        <div id="home-activity"><p class="muted">Loading…</p></div>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals</h2>
      <div id="home-approvals"><p class="muted">Loading…</p></div>
    </div>

    <style>
      /* KPI cards are canonical in style.css (.kpi/.kpi-grid) — shared across
         Overview, this page, and Jackpot overview. Only page-local layout below. */
      .live-round {
        display: grid;
        grid-template-columns: 110px 1fr;
        gap: 6px 14px;
        font-size: 13px;
      }
      .live-round dt { color: var(--text-dim); }
      .live-round dd { margin: 0; font-variant-numeric: tabular-nums; }
      .activity-row {
        display: grid;
        grid-template-columns: 90px 1fr;
        gap: 4px 12px;
        font-size: 12px;
        padding: 6px 0;
        border-bottom: 1px solid var(--border);
      }
      .activity-row:last-child { border-bottom: none; }
      .activity-row .time { color: var(--text-dim); }
      .activity-row .body code { font-size: 11px; }
    </style>
  `;

  const kpisEl = root.querySelector<HTMLElement>('#home-kpis')!;
  const roundEl = root.querySelector<HTMLElement>('#home-round')!;
  const activityEl = root.querySelector<HTMLElement>('#home-activity')!;
  const approvalsEl = root.querySelector<HTMLElement>('#home-approvals')!;

  async function fetchActiveRound(): Promise<ActiveRoundLite | null> {
    const res = await fetch(
      `${deps.roundEngineBaseUrl}/v1/rounds/active?integratorId=${deps.integratorId}&gameId=greedyking`,
    );
    if (!res.ok) return null;
    const body = (await res.json()) as ActiveRoundLite | null;
    return body;
  }

  async function refresh(): Promise<void> {
    try {
      // Every panel falls back independently. This dashboard is assembled from
      // surfaces guarded by DIFFERENT capabilities, so a staff member who can
      // see activity but not revenue would otherwise get one 403 that blanks
      // the entire page rather than the single tile they cannot read.
      const [ggr, pending, activity, round, drift, failedCalls] = await Promise.all([
        deps.client
          .fetchGgrReport(deps.integratorId, { since: '24h' })
          .catch(() => null),
        deps.client.listApprovals(deps.integratorId, 'pending').catch(() => []),
        deps.client
          .listAuditLog({ integratorId: deps.integratorId, limit: 10 })
          .catch(() => ({ entries: [] })),
        fetchActiveRound().catch(() => null),
        deps.client.listWalletDrift(deps.integratorId, { status: 'unresolved', limit: 1 }).catch(() => ({
          unresolvedCount: 0,
          entries: [],
        })),
        deps.client
          .listWalletTransactions(deps.integratorId, { status: 'failed', limit: 200 })
          .catch(() => ({ entries: [] })),
      ]);
      kpisEl.innerHTML = renderKpis(ggr, pending, drift.unresolvedCount, failedCalls.entries.length);
      roundEl.innerHTML = renderRound(round);
      activityEl.innerHTML = renderActivity(activity.entries);
      approvalsEl.innerHTML = renderApprovals(pending);
    } catch (err) {
      kpisEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
    }
  }

  function renderKpis(
    /** Null when the viewer lacks revenue reporting — the money tiles are simply omitted. */
    ggr: GgrReport | null,
    pending: ApprovalView[],
    unresolvedDrift: number,
    failedCalls: number,
  ): string {
    const num = (s: string): string => fmtNumber(s);
    const pct = (n: number): string => `${(n * 100).toFixed(2)}%`;
    const houseColor = ggr && Number(ggr.houseTake) >= 0 ? 'var(--success)' : 'var(--error)';
    // Integrator-scoped deep links — keep the integrator sidebar/scope intact.
    const gh = (sub: string): string =>
      `/integrators/${encodeURIComponent(deps.integratorId)}/${sub}`;
    return `
      <a class="kpi" href="${gh('approvals')}">
        <div class="kpi-label">Pending approvals</div>
        <div class="kpi-value" style="color:${pending.length > 0 ? 'var(--warn)' : 'var(--text)'}">${pending.length}</div>
        <div class="kpi-sub">${pending.length > 0 ? 'awaiting a second admin' : 'all clear'}</div>
      </a>
      ${ggr ? `
      <a class="kpi" href="${gh('reports/pl')}">
        <div class="kpi-label">GGR (24h)</div>
        <div class="kpi-value" style="color:${houseColor}">${num(ggr.houseTake)}</div>
        <div class="kpi-sub">RTP ${pct(ggr.rtp)}</div>
      </a>
      <a class="kpi" href="${gh('reports/pl')}">
        <div class="kpi-label">Total bet (24h)</div>
        <div class="kpi-value">${num(ggr.totalBet)}</div>
        <div class="kpi-sub">${ggr.betCount.toLocaleString()} bets</div>
      </a>
      <a class="kpi" href="${gh('players')}">
        <div class="kpi-label">Distinct players (24h)</div>
        <div class="kpi-value">${ggr.distinctPlayers}</div>
      </a>
      ` : ''}
      <a class="kpi" href="${gh(`wallet-drift${unresolvedDrift === 0 && failedCalls > 0 ? '?view=failed' : ''}`)}">
        <div class="kpi-label">Reconcile needed</div>
        <div class="kpi-value" style="color:${unresolvedDrift > 0 || failedCalls > 0 ? 'var(--warn)' : 'var(--text)'}">${unresolvedDrift + failedCalls}</div>
        <div class="kpi-sub">${unresolvedDrift} drift · ${failedCalls} failed calls</div>
      </a>
    `;
  }

  function renderRound(round: ActiveRoundLite | null): string {
    if (!round) return '<p class="muted">No active round.</p>';
    const now = Date.now();
    const remain = Math.max(0, round.bettingClosesAt - now);
    return `<dl class="live-round">
      <dt>Game</dt><dd><code>${round.gameId}</code></dd>
      <dt>State</dt><dd><span class="pill ${round.state === 'betting_open' ? 'live' : ''}">${round.state}</span></dd>
      <dt>Round ID</dt><dd><code>${round.id.slice(-10)}</code></dd>
      <dt>Started</dt><dd>${fmtTime(round.startedAt)}</dd>
      <dt>Bet closes</dt><dd>${remain > 0 ? `${(remain / 1000).toFixed(1)}s` : '—'}</dd>
    </dl>`;
  }

  function renderActivity(entries: AuditEntry[]): string {
    if (entries.length === 0) return '<p class="muted">No recent activity.</p>';
    return entries
      .map(
        (e) => `<div class="activity-row">
          <div class="time">${fmtTime(e.createdAt)}</div>
          <div class="body">
            <code>${escapeHtml(e.action)}</code> by ${escapeHtml(e.actorEmail)}
            <div class="muted" style="font-size:11px">${escapeHtml(e.targetType)} · ${escapeHtml(e.targetId.slice(0, 32))}${e.targetId.length > 32 ? '…' : ''}</div>
          </div>
        </div>`,
      )
      .join('');
  }

  function renderApprovals(pending: ApprovalView[]): string {
    if (pending.length === 0) return '<p class="muted">Nothing waiting for review.</p>';
    return `<table>
      <thead><tr><th>Created</th><th>Surface</th><th>Game</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${pending
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.surface}</code></td>
            <td><code>${a.gameId}</code></td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><a href="/approvals">Review →</a></td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
  }

  await refresh();
  // No background poll. Refresh on tab focus instead — covers "user came
  // back from playing" without hammering the API every 15s.
  const onVis = (): void => {
    if (document.visibilityState === 'visible') void refresh();
  };
  document.addEventListener('visibilitychange', onVis);
  const cleanup = new MutationObserver(() => {
    if (!root.querySelector('#home-kpis')) {
      document.removeEventListener('visibilitychange', onVis);
      cleanup.disconnect();
    }
  });
  cleanup.observe(root, { childList: true });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
